import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

/**
 * OfflineNotice — a calm, full-width banner shown when the device loses its
 * connection. Deliberately non-blocking: everything already loaded stays
 * usable (journal writing queues locally), so this only explains what is
 * happening rather than taking the screen away.
 */
export function OfflineNotice() {
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    const sync = () => setOffline(!navigator.onLine);
    sync();
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-card/95 px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 backdrop-blur"
    >
      <div className="mx-auto flex max-w-2xl items-start gap-3">
        <WifiOff className="mt-0.5 h-5 w-5 shrink-0 text-[var(--gold-deep)]" />
        <div className="text-sm">
          <p className="font-medium text-foreground">You&apos;re offline</p>
          <p className="mt-0.5 text-muted-foreground">
            Pages you&apos;ve already opened still work, and anything you write
            is kept safely on this device until the connection returns.
          </p>
        </div>
      </div>
    </div>
  );
}
